import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Clock, Timer, CheckCircle2, Activity, ChevronRight, Check } from "lucide-react";
import { fmtDate, fmtDateTime } from "@/lib/constants";
import { MonthPicker, currentMonth, monthRange, monthLabel } from "@/components/MonthPicker";
import { RiepilogoPdfButton, DistaccamentoFilter, ProvinciaFilter } from "@/components/RiepilogoPdf";

function DetailDialog({ gid, mese, onClose }) {
  const { data } = useQuery({
    queryKey: ["presenze-detail", gid, mese],
    queryFn: async () => (await api.get(`/presenze/${gid}`, { params: monthRange(mese) })).data,
    enabled: !!gid,
  });
  return (
    <Dialog open={!!gid} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto gz-scroll">
        <DialogHeader>
          <DialogTitle>
            Presenze — {data?.guardia?.nome || "…"} · {monthLabel(mese)}
            {data && <span className="ml-2 text-sm font-normal text-slate-500">{data.ore_totali} ore</span>}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-2 py-2">
          {!data?.turni?.length ? (
            <p className="py-6 text-center text-sm text-slate-400">Nessun turno nel mese.</p>
          ) : (
            data.turni.map((t) => (
              <div key={t.id} className="flex items-center justify-between rounded-lg border border-slate-200 p-3 text-sm">
                <div>
                  <p className="font-semibold text-slate-900">{fmtDate(t.data)} · {t.ora_inizio}–{t.ora_fine}</p>
                  <p className="text-xs text-slate-500">
                    {t.check_in ? `In: ${fmtDateTime(t.check_in)}` : "Nessun check-in"}
                    {t.check_out ? ` · Out: ${fmtDateTime(t.check_out)}` : ""}
                  </p>
                </div>
                <Badge variant="outline" className={t.ore > 0 ? "border-green-200 bg-green-50 text-green-800" : "border-slate-200 bg-slate-50 text-slate-500"}>
                  {t.ore > 0 ? `${t.ore} h` : "—"}
                </Badge>
              </div>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Presenze() {
  const { user } = useAuth();
  const isStaff = user?.role !== "guardia";
  const [detailGid, setDetailGid] = useState(null);
  const [mese, setMese] = useState(currentMonth());
  const [distacc, setDistacc] = useState("tutti");
  const [prov, setProv] = useState("tutte");
  const canProv = ["admin", "coordinatore"].includes(user?.role);

  const { data = [], isLoading } = useQuery({
    queryKey: ["presenze", mese, distacc, prov],
    queryFn: async () => (await api.get("/presenze", { params: { ...monthRange(mese), ...(distacc !== "tutti" ? { distaccamento: distacc } : {}), ...(prov !== "tutte" ? { provincia: prov } : {}) } })).data,
  });

  const totOre = data.reduce((s, r) => s + r.ore_totali, 0).toFixed(1);
  const totCompletati = data.reduce((s, r) => s + r.turni_completati, 0);
  const totInServizio = data.reduce((s, r) => s + r.in_servizio, 0);

  const stats = [
    { label: "Ore di servizio", value: totOre, icon: Timer, color: "text-[#166534]", bg: "bg-emerald-50" },
    { label: "Turni completati", value: totCompletati, icon: CheckCircle2, color: "text-blue-600", bg: "bg-blue-50" },
    { label: "In servizio ora", value: totInServizio, icon: Activity, color: "text-amber-600", bg: "bg-amber-50" },
  ];

  return (
    <div className="gz-fade-up">
      <div className="mb-6">
        <h1 className="font-heading text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl">Monitoraggio presenze</h1>
        <p className="mt-1 text-sm text-slate-500">
          {isStaff ? "Ore di servizio e presenze per ogni guardia (dai check-in/out sui turni)." : "Le tue ore di servizio e presenze."}
        </p>
      </div>

      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <MonthPicker value={mese} onChange={setMese} testid="presenze-month" />
        <div className="flex flex-wrap items-center gap-2">
          {canProv && <ProvinciaFilter value={prov} onChange={setProv} testid="presenze-provincia-filter" />}
          {isStaff && <DistaccamentoFilter value={distacc} onChange={setDistacc} testid="presenze-distaccamento-filter" />}
          <RiepilogoPdfButton mese={mese} distaccamento={distacc} provincia={prov} testid="presenze-riepilogo-pdf" />
        </div>
      </div>

      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
        {stats.map((s) => (
          <Card key={s.label} data-testid={`presenze-stat-${s.label.toLowerCase().replace(/[^a-z]/g, "-")}`} className="border-slate-200 shadow-sm">
            <CardContent className="flex items-center gap-4 p-5">
              <div className={`flex h-11 w-11 items-center justify-center rounded-lg ${s.bg}`}>
                <s.icon className={`h-5 w-5 ${s.color}`} />
              </div>
              <div>
                <p className="font-heading text-2xl font-extrabold text-slate-900">{s.value}</p>
                <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{s.label}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {isLoading ? (
        <p className="text-sm text-slate-400">Caricamento…</p>
      ) : data.length === 0 ? (
        <Card className="border-dashed"><CardContent className="py-12 text-center text-sm text-slate-400">
          <Clock className="mx-auto mb-3 h-8 w-8 text-slate-300" />
          Nessuna presenza a {monthLabel(mese)}. Le ore compaiono dopo i check-in/out sui turni.
        </CardContent></Card>
      ) : (
        <>
        <p className="mb-2 flex items-center gap-2 text-xs text-slate-500" data-testid="presenze-legenda-verde"><span className="inline-block h-3 w-3 rounded-sm bg-green-200" /> In verde chi ha fatto almeno 4 servizi completati nel mese</p>
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3">Guardia</th>
                <th className="px-4 py-3">Distaccamento</th>
                <th className="px-4 py-3">Turni</th>
                <th className="px-4 py-3">Completati</th>
                <th className="px-4 py-3">Ore totali</th>
                <th className="px-4 py-3">Ultimo check-in</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.map((r) => (
                <tr
                  key={r.guardia_id}
                  data-testid={`presenze-row-${r.guardia_id}`}
                  className={`cursor-pointer transition-colors ${r.turni_completati >= 4 ? "bg-green-100 hover:bg-green-200/70" : "hover:bg-emerald-50/50"}`}
                  onClick={() => setDetailGid(r.guardia_id)}
                >
                  <td className="px-4 py-3 font-semibold text-slate-900">
                    {r.guardia_nome}
                    {r.in_servizio > 0 && (
                      <span className="ml-2 inline-flex items-center gap-1 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold text-amber-800">
                        <Activity className="h-3 w-3" /> IN SERVIZIO
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-600">{r.distaccamento || "—"}</td>
                  <td className="px-4 py-3 text-slate-700">{r.turni_totali}</td>
                  <td className="px-4 py-3 text-slate-700">
                    {r.turni_completati >= 4
                      ? <span data-testid={`presenze-4servizi-${r.guardia_id}`} className="inline-flex items-center gap-1 rounded-full bg-[#166534] px-2 py-0.5 text-xs font-bold text-white"><Check className="h-3 w-3" /> {r.turni_completati}</span>
                      : r.turni_completati}
                  </td>
                  <td className="px-4 py-3 font-mono-code font-semibold text-[#166534]">{r.ore_totali} h</td>
                  <td className="px-4 py-3 text-slate-500">{r.ultimo_checkin ? fmtDateTime(r.ultimo_checkin) : "—"}</td>
                  <td className="px-4 py-3 text-right"><ChevronRight className="h-4 w-4 text-slate-400" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </>
      )}

      {detailGid && <DetailDialog gid={detailGid} mese={mese} onClose={() => setDetailGid(null)} />}
    </div>
  );
}
